// src/features/campus-guesser/services/locationService.js
import { db } from '../../../core/config/firebase';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { getEasternDate } from '../../wordle/utils/dailyWordUtils';

const LOCATIONS_COLLECTION = 'campusLocations';
const DAILY_LOCATIONS_COLLECTION = 'dailyLocations';

/**
 * Create a new campus location in the database
 * @param {Object} locationData
 * @param {string} locationData.imageStoragePath - Firebase Storage path
 * @param {string} locationData.imageUrl - Download URL for the image
 * @param {Object} locationData.actualLocation - {lat, lng, name}
 * @param {string} locationData.difficulty - Optional difficulty rating
 * @param {Array<string>} locationData.tags - Optional tags
 * @returns {Promise<string>} The document ID of the created location
 */
export async function createLocation(locationData) {
  try {
    const locationDoc = {
      imageStoragePath: locationData.imageStoragePath,
      imageUrl: locationData.imageUrl,
      actualLocation: {
        lat: locationData.actualLocation.lat,
        lng: locationData.actualLocation.lng,
        name: locationData.actualLocation.name || ''
      },
      difficulty: locationData.difficulty || 'medium',
      tags: locationData.tags || [],
      isActive: true,
      lastUsed: null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, LOCATIONS_COLLECTION), locationDoc);
    console.log('✅ Location created with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Error creating location:', error);
    throw error;
  }
}

/**
 * Get a specific location by ID
 * @param {string} locationId
 * @returns {Promise<Object|null>}
 */
export async function getLocation(locationId) {
  try {
    const docRef = doc(db, LOCATIONS_COLLECTION, locationId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('❌ Error getting location:', error);
    throw error;
  }
}

/**
 * Get all campus locations
 * @param {Object} options
 * @param {boolean} options.activeOnly - Only return active locations
 * @param {string} options.orderByField - Field to order by
 * @returns {Promise<Array>}
 */
export async function getAllLocations({ activeOnly = false, orderByField = 'createdAt' } = {}) {
  try {
    let q;

    if (activeOnly) {
      // When filtering by isActive, create compound query
      q = query(
        collection(db, LOCATIONS_COLLECTION),
        where('isActive', '==', true),
        orderBy(orderByField, 'desc')
      );
    } else {
      // When showing all, just order by field
      q = query(
        collection(db, LOCATIONS_COLLECTION),
        orderBy(orderByField, 'desc')
      );
    }

    const querySnapshot = await getDocs(q);
    const locations = [];

    querySnapshot.forEach((doc) => {
      locations.push({ id: doc.id, ...doc.data() });
    });

    return locations;
  } catch (error) {
    console.error('❌ Error getting all locations:', error);

    // If it's an index error, provide helpful message
    if (error.code === 'failed-precondition') {
      console.error('Firestore index required. Creating query without orderBy...');
      // Fallback: get all docs without ordering
      try {
        const fallbackQuery = activeOnly
          ? query(collection(db, LOCATIONS_COLLECTION), where('isActive', '==', true))
          : collection(db, LOCATIONS_COLLECTION);

        const querySnapshot = await getDocs(fallbackQuery);
        const locations = [];
        querySnapshot.forEach((doc) => {
          locations.push({ id: doc.id, ...doc.data() });
        });
        // Sort in memory
        return locations.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);
      } catch (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
        throw fallbackError;
      }
    }
    throw error;
  }
}

/**
 * Update a location
 * @param {string} locationId
 * @param {Object} updates
 * @returns {Promise<void>}
 */
export async function updateLocation(locationId, updates) {
  try {
    const docRef = doc(db, LOCATIONS_COLLECTION, locationId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
    console.log('✅ Location updated:', locationId);
  } catch (error) {
    console.error('❌ Error updating location:', error);
    throw error;
  }
}

/**
 * Delete a location (soft delete by setting isActive to false)
 * @param {string} locationId
 * @returns {Promise<void>}
 */
export async function deleteLocation(locationId) {
  try {
    const docRef = doc(db, LOCATIONS_COLLECTION, locationId);
    await updateDoc(docRef, {
      isActive: false,
      updatedAt: Timestamp.now()
    });
    console.log('✅ Location soft-deleted:', locationId);
  } catch (error) {
    console.error('❌ Error deleting location:', error);
    throw error;
  }
}

/**
 * Permanently delete a location from the database
 * @param {string} locationId
 * @returns {Promise<void>}
 */
export async function permanentlyDeleteLocation(locationId) {
  try {
    const docRef = doc(db, LOCATIONS_COLLECTION, locationId);
    await deleteDoc(docRef);
    console.log('✅ Location permanently deleted:', locationId);
  } catch (error) {
    console.error('❌ Error permanently deleting location:', error);
    throw error;
  }
}

/**
 * Get today's daily location (or create one if it doesn't exist)
 * Uses atomic transaction to prevent race conditions
 * @returns {Promise<Object>} The daily location data
 */
export async function getTodaysDailyLocation() {
  try {
    const dateString = getEasternDate();
    const dailyDocRef = doc(db, DAILY_LOCATIONS_COLLECTION, dateString);

    const result = await runTransaction(db, async (transaction) => {
      const dailyDoc = await transaction.get(dailyDocRef);

      if (dailyDoc.exists()) {
        // Today's location already exists
        return { id: dailyDoc.id, ...dailyDoc.data() };
      }

      // Need to select a new location for today
      // Get all active locations that haven't been used recently
      const locationsQuery = query(
        collection(db, LOCATIONS_COLLECTION),
        where('isActive', '==', true),
        orderBy('lastUsed', 'asc'),
        limit(10)
      );

      const locationsSnapshot = await getDocs(locationsQuery);

      if (locationsSnapshot.empty) {
        throw new Error('No active locations available');
      }

      // Pick a random location from the top 10 least recently used
      const availableLocations = [];
      locationsSnapshot.forEach((doc) => {
        availableLocations.push({ id: doc.id, ...doc.data() });
      });

      const selectedLocation = availableLocations[Math.floor(Math.random() * availableLocations.length)];

      // Create daily location document
      const dailyLocationData = {
        locationId: selectedLocation.id,
        imageStoragePath: selectedLocation.imageStoragePath,
        imageUrl: selectedLocation.imageUrl,
        actualLocation: selectedLocation.actualLocation,
        date: Timestamp.now(),
        dateString: dateString,
        createdAt: Timestamp.now(),
        totalAttempts: 0
      };

      // Write daily location
      transaction.set(dailyDocRef, dailyLocationData);

      // Update the location's lastUsed timestamp
      const locationRef = doc(db, LOCATIONS_COLLECTION, selectedLocation.id);
      transaction.update(locationRef, { lastUsed: Timestamp.now() });

      return { id: dateString, ...dailyLocationData };
    });

    console.log('✅ Daily location retrieved:', result.locationId);
    return result;
  } catch (error) {
    console.error('❌ Error getting daily location:', error);
    throw error;
  }
}

/**
 * Manually set a location for a specific date (admin function)
 * @param {string} dateString - Format: YYYY-MM-DD
 * @param {string} locationId - The location to use for this date
 * @returns {Promise<void>}
 */
export async function setDailyLocation(dateString, locationId) {
  try {
    // Get the location data
    const location = await getLocation(locationId);
    if (!location) {
      throw new Error('Location not found');
    }

    // Create or update daily location document
    const dailyDocRef = doc(db, DAILY_LOCATIONS_COLLECTION, dateString);

    const dailyLocationData = {
      locationId: location.id,
      imageStoragePath: location.imageStoragePath,
      imageUrl: location.imageUrl,
      actualLocation: location.actualLocation,
      date: Timestamp.now(),
      dateString: dateString,
      createdAt: Timestamp.now(),
      totalAttempts: 0,
      manuallySet: true
    };

    await updateDoc(dailyDocRef, dailyLocationData).catch(async () => {
      // Document doesn't exist, create it
      await addDoc(collection(db, DAILY_LOCATIONS_COLLECTION), dailyLocationData);
    });

    console.log('✅ Daily location set for', dateString);
  } catch (error) {
    console.error('❌ Error setting daily location:', error);
    throw error;
  }
}
